const GROUP_LABELS = {
  _root: 'Document',
  accounting_customer_party: 'Customer party',
  accounting_supplier_party: 'Supplier party',
  buyer_customer_party: 'Buyer customer party',
  seller_supplier_party: 'Seller supplier party',
  payee_party: 'Payee party',
  tax_representative_party: 'Tax representative party',
  delivery: 'Delivery',
  invoice_period: 'Invoice period',
  order_reference: 'Order reference',
  contract_document_reference: 'Contract reference',
  despatch_document_reference: 'Despatch reference',
  receipt_document_reference: 'Receipt reference',
  statement_document_reference: 'Statement reference',
  originator_document_reference: 'Originator reference',
  payment_means: 'Payment means',
  payment_terms: 'Payment terms',
  tax_exchange_rate: 'Tax exchange rate',
  project_reference: 'Project reference',
  classified_tax_category: 'Tax category',
  price: 'Price',
  order_line_reference: 'Order line reference',
  despatch_line_reference: 'Despatch line reference',
  document_reference: 'Document reference',
  buyer_item_identification: 'Buyer item ID',
  seller_item_identification: 'Seller item ID',
  standard_item_identification: 'Standard item ID',
  additional_item_identification: 'Additional item ID',
  origin_country: 'Origin country',
  commodity_classification: 'Commodity classification',
  additional_item_property: 'Additional item property',
  tax_category: 'Tax category',
  card_account: 'Card account',
  payee_financial_account: 'Payee financial account',
  payment_mandate: 'Payment mandate',
  document_lines: 'Document lines',
  charges: 'Charges',
  allowances: 'Allowances',
  billing_reference: 'Billing reference',
  attachments: 'Attachments',
};

export const getFieldGroupKey = (path) => {
  if (!path || typeof path !== 'string') {
    return '_root';
  }

  const dotIndex = path.indexOf('.');

  if (dotIndex === -1) {
    return '_root';
  }

  return path.slice(0, dotIndex);
};

export const getFieldGroupLabel = (groupKey, parentLabel = null) => {
  if (parentLabel && groupKey === '_root') {
    return 'Fields';
  }

  if (GROUP_LABELS[groupKey]) {
    return GROUP_LABELS[groupKey];
  }

  return groupKey
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const groupPathEntries = (entries, { parentLabel = null } = {}) => {
  const groups = new Map();

  for (const entry of entries) {
    const key = getFieldGroupKey(entry.path);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: getFieldGroupLabel(key, parentLabel),
        entries: [],
      });
    }

    groups.get(key).entries.push(entry);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === '_root') {
      return -1;
    }

    if (b.key === '_root') {
      return 1;
    }

    return a.label.localeCompare(b.label);
  });
};

export const countMappedEntries = (entries, rawConfig) =>
  entries.filter((entry) => getMappingStatus(entry.path, rawConfig) === 'configured').length;

export const shouldExpandFieldGroup = (entries, rawConfig) => {
  const mappedCount = countMappedEntries(entries, rawConfig);

  if (mappedCount > 0) {
    return true;
  }

  return entries.some((entry) => entry.required);
};

const classifyNode = (node) => {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(node, 'fields')) {
    return typeof node.source === 'string' ? 'array' : 'object';
  }

  return 'field';
};

export const getNodeAtPath = (fields, path) => {
  if (!fields || !path) {
    return null;
  }

  const segments = path.split('.');
  let current = fields;

  for (const segment of segments) {
    const node = current?.[segment];
    if (!node) {
      return null;
    }

    if (segments[segments.length - 1] === segment) {
      return node;
    }

    if (classifyNode(node) === 'field') {
      return null;
    }

    current = node.fields;
  }

  return null;
};

export const getMappingStatus = (path, rawConfig) => {
  const node = getNodeAtPath(rawConfig?.fields, path);

  if (!node || typeof node !== 'object') {
    return 'unconfigured';
  }

  const kind = classifyNode(node);

  if (kind === 'field') {
    return 'configured';
  }

  return 'configured';
};

export const flattenCatalogTree = (fields, prefix = '') => {
  const entries = [];

  for (const [key, node] of Object.entries(fields || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    entries.push({
      path,
      kind: node.kind,
      required: Boolean(node.required),
      label: getFieldGroupLabel(key, prefix || null),
    });

    if (node.children) {
      entries.push(...flattenCatalogTree(node.children, path));
    }
  }

  return entries;
};

/** Marmin API path used for SFTP folder routing (not always present in transform field catalogs). */
export const PATH_PARTY_BY_TARGET = {
  'marmin.sale.invoice': 'accounting_supplier_party',
  'marmin.sale.credit-note': 'accounting_supplier_party',
  'marmin.purchase.invoice': 'accounting_customer_party',
  'marmin.purchase.credit-note': 'accounting_customer_party',
};

const SFTP_PATH_PARTY_LEAF_KEYS = ['profile_id', 'tin'];

function buildSftpPathPartyOverlayNode() {
  return {
    kind: 'object',
    required: false,
    sftpPathParty: true,
    children: {
      profile_id: {
        kind: 'field',
        required: false,
        sftpPathParty: true,
      },
      tin: {
        kind: 'field',
        required: false,
        sftpPathParty: true,
      },
    },
  };
}

/**
 * Inject SFTP path-party fields into the Marmin catalog for Simple mapper
 * (sale catalogs omit accounting_supplier_party; purchase may omit customer path party).
 */
export function mergeSftpPathPartyCatalog(catalog, documentType) {
  if (!catalog?.fields) {
    return catalog;
  }

  const partyKey = PATH_PARTY_BY_TARGET[documentType];
  if (!partyKey) {
    return catalog;
  }

  const existing = catalog.fields[partyKey];
  const overlay = buildSftpPathPartyOverlayNode();

  if (!existing) {
    return {
      ...catalog,
      fields: {
        ...catalog.fields,
        [partyKey]: overlay,
      },
    };
  }

  const mergedChildren = { ...(existing.children || {}) };

  SFTP_PATH_PARTY_LEAF_KEYS.forEach((leafKey) => {
    if (!mergedChildren[leafKey]) {
      mergedChildren[leafKey] = overlay.children[leafKey];
    } else {
      mergedChildren[leafKey] = { ...mergedChildren[leafKey], sftpPathParty: true };
    }
  });

  return {
    ...catalog,
    fields: {
      ...catalog.fields,
      [partyKey]: {
        ...existing,
        sftpPathParty: true,
        children: mergedChildren,
      },
    },
  };
}

export const normalizeFieldCatalog = (catalog) => {
  if (!catalog?.fields) {
    return { tree: {}, flatEntries: [] };
  }

  return {
    tree: catalog.fields,
    flatEntries: flattenCatalogTree(catalog.fields),
  };
};

export const filterPathEntries = (entries, query) => {
  const normalizedQuery = (query || '').trim().toLowerCase();
  if (!normalizedQuery) {
    return entries;
  }

  return entries.filter((entry) => entry.path.toLowerCase().includes(normalizedQuery));
};

export const parseRawConfigFromText = (configJsonText) => {
  if (!configJsonText || !configJsonText.trim()) {
    return null;
  }

  try {
    return JSON.parse(configJsonText);
  } catch {
    return null;
  }
};

export const buildQualifiedPath = (...segments) => segments.filter(Boolean).join('.');

export const getCatalogChildren = (tree, path = '') => {
  if (!path) {
    return tree || {};
  }

  const segments = path.split('.');
  let current = tree;

  for (const segment of segments) {
    const node = current?.[segment];
    if (!node?.children) {
      return {};
    }

    current = node.children;
  }

  return current || {};
};

export const getCatalogNode = (tree, path) => {
  if (!path) {
    return null;
  }

  const segments = path.split('.');
  let current = tree;

  for (const segment of segments) {
    const node = current?.[segment];
    if (!node) {
      return null;
    }

    if (segments[segments.length - 1] === segment) {
      return node;
    }

    current = node.children;
  }

  return null;
};

function buildFieldsFromManifestTree(manifestFields) {
  const fields = {};

  for (const [key, node] of Object.entries(manifestFields || {})) {
    if (node.kind === 'field') {
      fields[key] = { default: '' };

      continue;
    }

    if (node.kind === 'object') {
      fields[key] = {
        fields: buildFieldsFromManifestTree(node.children),
      };

      continue;
    }

    if (node.kind === 'array') {
      fields[key] = {
        source: '$.__line_items__',
        fields: buildFieldsFromManifestTree(node.children),
      };
    }
  }

  return fields;
}

export const buildInitialFieldsFromManifest = (manifest) => buildFieldsFromManifestTree(manifest?.fields);
